import { useMemo } from 'react';
import { VisitorVendorState } from '../../../../api/visitor-vendor-state/visitor-vendor-state.api';

export interface SyncStatusData {
  status: 'success' | 'error' | 'unknown';
  timestamp: string | null;
  title: string;
  request: object | null;
  response: object | null;
  error: string | null;
}

/**
 * Custom hook that centralizes sync status logic for vendor states.
 * Determines the most recent sync result and provides formatted data for UI components.
 */
export const useSyncStatus = (
  vendorState: VisitorVendorState
): SyncStatusData | null => {
  return useMemo(() => {
    const hasSuccessful = !!vendorState.last_successful_at;
    const hasFailed = !!vendorState.last_failed_at;

    if (!hasSuccessful && !hasFailed) {
      return null;
    }

    // Show the most recent sync result
    const showSuccess =
      hasSuccessful &&
      (!hasFailed ||
        new Date(vendorState.last_successful_at!) >
          new Date(vendorState.last_failed_at!));

    if (showSuccess) {
      return {
        status: 'success',
        timestamp: vendorState.last_successful_at!,
        title: 'Last Successful Sync',
        request: vendorState.last_successful_request,
        response: vendorState.last_successful_response,
        error: null
      };
    } else if (hasFailed) {
      return {
        status: 'error',
        timestamp: vendorState.last_failed_at!,
        title: 'Last Failed Sync',
        request: vendorState.last_failed_request,
        response: vendorState.last_failed_response,
        error: vendorState.last_error_message
      };
    }

    return null;
  }, [
    vendorState.last_successful_at,
    vendorState.last_failed_at,
    vendorState.last_successful_request,
    vendorState.last_successful_response,
    vendorState.last_failed_request,
    vendorState.last_failed_response,
    vendorState.last_error_message
  ]);
};

/**
 * Hook that determines if the last sync was successful (for badge styling)
 */
export const useLastSyncStatus = (
  vendorState: VisitorVendorState
): {
  isSuccess: boolean;
  timestamp: string | null;
} => {
  return useMemo(() => {
    const hasSuccessful = !!vendorState.last_successful_at;
    const hasFailed = !!vendorState.last_failed_at;

    if (!hasSuccessful && !hasFailed) {
      return { isSuccess: false, timestamp: null };
    }

    const isSuccess =
      hasSuccessful &&
      (!hasFailed ||
        new Date(vendorState.last_successful_at!) >
          new Date(vendorState.last_failed_at!));

    return {
      isSuccess,
      timestamp: isSuccess
        ? vendorState.last_successful_at!
        : vendorState.last_failed_at!
    };
  }, [vendorState.last_successful_at, vendorState.last_failed_at]);
};
